import { useState } from "react";
import { analyzeChallengeMap, type AnalyzeItem } from "../../../mocks/classContent";

interface Props {
  classId: number;
  onComplete: (result: string) => void;
}

/* ─── Error type badge ─── */
function ErrorTypeBadge({ type }: { type?: string }) {
  if (!type) return null;
  const map: Record<string, { label: string; color: string; bg: string }> = {
    hallucination: { label: "AI 환각", color: "#E53935", bg: "#FFEBEE" },
    factual:       { label: "팩트 오류", color: "#E65100", bg: "#FFF3E0" },
    bias:          { label: "편향 정보", color: "#6A1B9A", bg: "#F3E5F5" },
  };
  const style = map[type];
  if (!style) return null;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold"
      style={{ color: style.color, backgroundColor: style.bg }}
    >
      <i className="ri-error-warning-fill text-[10px]" />
      {style.label}
    </span>
  );
}

/* ─── Item card ─── */
function AnalyzeItemCard({
  item,
  index,
  isSelected,
  revealed,
  onToggle,
}: {
  item: AnalyzeItem;
  index: number;
  isSelected: boolean;
  revealed: boolean;
  onToggle: () => void;
}) {
  const letter = ["A", "B", "C", "D", "E"][index];

  let cardStyle = "border-2 border-[#E8E9EB] bg-white";
  let badgeStyle = "bg-[#F2F4F6] text-[#8B95A1]";

  if (revealed) {
    if (item.isError) {
      cardStyle = "border-2 border-red-300 bg-red-50";
      badgeStyle = isSelected
        ? "bg-[#00D873] text-white"
        : "bg-red-400 text-white";
    } else {
      cardStyle = isSelected
        ? "border-2 border-orange-300 bg-orange-50"
        : "border-2 border-[#E8E9EB] bg-white";
      badgeStyle = isSelected ? "bg-orange-400 text-white" : "bg-[#F2F4F6] text-[#8B95A1]";
    }
  } else if (isSelected) {
    cardStyle = "border-2 border-red-400 bg-red-50";
    badgeStyle = "bg-red-500 text-white";
  }

  return (
    <button
      onClick={() => !revealed && onToggle()}
      disabled={revealed}
      className={`w-full text-left rounded-2xl p-4 transition-all duration-200 cursor-pointer ${cardStyle} ${
        !revealed ? "hover:border-red-300 hover:bg-red-50/50 active:scale-[0.99]" : "cursor-default"
      }`}
    >
      <div className="flex items-start gap-3">
        {/* Letter badge */}
        <div
          className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-extrabold transition-all ${badgeStyle}`}
        >
          {revealed && isSelected && item.isError ? (
            <i className="ri-check-line text-sm" />
          ) : revealed && isSelected && !item.isError ? (
            <i className="ri-close-line text-sm" />
          ) : revealed && !isSelected && item.isError ? (
            <i className="ri-alert-line text-sm" />
          ) : (
            letter
          )}
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-[#191F28] leading-snug mb-1">
            {item.statement}
          </p>

          {/* Revealed: show result */}
          {revealed && (
            <div className="mt-2 space-y-1.5">
              {item.isError && (
                <div className="flex items-center gap-1.5">
                  <ErrorTypeBadge type={item.errorType} />
                  <span className="text-[10px] font-bold text-red-500">오류 발견!</span>
                </div>
              )}
              {!item.isError && isSelected && (
                <span className="text-[10px] font-bold text-orange-500">오답 — 이건 사실이에요</span>
              )}
              <p className="text-xs text-[#3D4756] leading-relaxed">{item.explanation}</p>
            </div>
          )}

          {/* Pre-reveal: selection hint */}
          {!revealed && isSelected && (
            <p className="text-[11px] text-red-500 font-medium mt-1">오류라고 표시됨 — 다시 클릭하면 해제돼요</p>
          )}
        </div>

        {/* Checkbox */}
        {!revealed && (
          <div
            className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-all mt-0.5 ${
              isSelected ? "bg-red-500 border-red-500" : "border-[#C4C9D4]"
            }`}
          >
            {isSelected && <i className="ri-check-line text-white text-xs" />}
          </div>
        )}
      </div>
    </button>
  );
}

/* ─── Score card ─── */
function ScoreCard({
  correct,
  total,
  falsePosCount,
  onNext,
}: {
  correct: number;
  total: number;
  falsePosCount: number;
  onNext: () => void;
}) {
  const pct = Math.round((correct / total) * 100);
  const isPerfect = correct === total && falsePosCount === 0;
  return (
    <div
      className={`rounded-2xl p-6 text-center mt-6 border-2 ${
        isPerfect ? "border-[#00D873] bg-[#F0FDF7]" : "border-[#E8E9EB] bg-white"
      }`}
    >
      <div className="text-4xl mb-3">{isPerfect ? "🎯" : pct >= 50 ? "🔍" : "📚"}</div>
      <p className="text-xl font-extrabold text-[#191F28] mb-1">
        {isPerfect ? "완벽한 팩트체커!" : pct >= 50 ? "절반 이상 맞혔어요!" : "AI를 더 꼼꼼히 봐야 해요"}
      </p>
      <p className="text-sm text-[#8B95A1] mb-5">
        오류 {correct}/{total}개 정확히 찾음
        {falsePosCount > 0 && ` · 잘못 표시 ${falsePosCount}개`}
      </p>

      <div className="flex justify-center gap-4 mb-6">
        <div className="text-center">
          <p className="text-2xl font-extrabold text-[#191F28]">{correct}</p>
          <p className="text-xs text-[#8B95A1]">오류 적발</p>
        </div>
        {falsePosCount > 0 && (
          <div className="text-center">
            <p className="text-2xl font-extrabold text-orange-500">{falsePosCount}</p>
            <p className="text-xs text-[#8B95A1]">오탐지</p>
          </div>
        )}
        <div className="text-center">
          <p className="text-2xl font-extrabold text-[#00D873]">{pct}%</p>
          <p className="text-xs text-[#8B95A1]">정확도</p>
        </div>
      </div>

      <div className="bg-[#191F28]/5 rounded-xl p-3 mb-5 text-left">
        <p className="text-xs font-bold text-[#191F28] mb-1 flex items-center gap-1.5">
          <i className="ri-lightbulb-line text-[#FF8C00]" />
          ACE 핵심 포인트
        </p>
        <p className="text-xs text-[#3D4756] leading-relaxed">
          AI가 만든 결과물을 그대로 믿지 않고 비판적으로 검증하는 능력이 바로 <strong>ACE의 A(Analyze)</strong>예요.
          이 스킬이 있어야 AI를 진짜 도구로 활용할 수 있어요.
        </p>
      </div>

      <button
        onClick={onNext}
        className="w-full py-4 bg-[#00D873] text-white font-extrabold text-base rounded-2xl cursor-pointer hover:opacity-90 transition-all hover:-translate-y-0.5 whitespace-nowrap"
        style={{ boxShadow: "0 6px 24px rgba(0,216,115,0.3)" }}
      >
        좋아요! 이제 직접 만들러 가기 →
      </button>
    </div>
  );
}

/* ─── Main Component ─── */
export default function AnalyzeChallengeStep({ classId, onComplete }: Props) {
  const challenge = analyzeChallengeMap[classId];
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [submitted, setSubmitted] = useState(false);

  if (!challenge) {
    onComplete("analyze-skipped");
    return null;
  }

  const toggle = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSubmit = () => {
    setSubmitted(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const actualErrors = challenge.items.filter((i) => i.isError);
  const correctHits = actualErrors.filter((i) => selectedIds.has(i.id)).length;
  const falsePosCount = [...selectedIds].filter((id) => {
    const item = challenge.items.find((i) => i.id === id);
    return item && !item.isError;
  }).length;

  return (
    <div className="bg-white rounded-2xl overflow-hidden mb-6">
      {/* Header */}
      <div className="px-8 pt-8 pb-0">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-search-eye-line text-red-500 text-lg" />
          </div>
          <span className="text-xs font-bold text-red-500 tracking-widest uppercase">Analyze 챌린지</span>
          <span className="ml-2 px-2.5 py-0.5 rounded-full bg-gradient-to-r from-red-500 to-orange-400 text-white text-[10px] font-extrabold">
            ACE — A 단계
          </span>
        </div>
        <h2 className="text-2xl font-extrabold text-[#191F28] mb-1">AI 오류 찾기 챌린지</h2>
        <p className="text-[#8B95A1] text-sm mb-6">
          아래 AI가 생성한 내용에서 <strong className="text-[#191F28]">오류 {challenge.errorCount}개</strong>를 찾아보세요.
          AI를 맹목적으로 믿지 않는 것이 진짜 실력이에요.
        </p>

        {/* Scenario banner */}
        <div className="bg-gradient-to-r from-[#FFF3E0] to-[#FFF9F0] border border-orange-200 rounded-2xl p-4 mb-6 flex gap-3">
          <div className="w-8 h-8 rounded-xl bg-orange-400 flex items-center justify-center flex-shrink-0 mt-0.5">
            <i className="ri-alarm-warning-line text-white text-sm" />
          </div>
          <div>
            <p className="text-[10px] font-bold text-orange-500 uppercase tracking-widest mb-0.5">상황 설명</p>
            <p className="text-sm text-[#3D4756] leading-relaxed">{challenge.scenario}</p>
          </div>
        </div>

        {/* AI Content block */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-6 h-6 rounded-lg bg-[#191F28] flex items-center justify-center">
              <i className="ri-robot-2-line text-white text-xs" />
            </div>
            <span className="text-sm font-bold text-[#191F28]">{challenge.aiContentTitle}</span>
            <span className="ml-auto flex items-center gap-1 text-[11px] text-[#8B95A1] font-medium">
              <i className="ri-sparkling-2-line text-orange-400" />
              {challenge.aiTool} 생성
            </span>
          </div>
          <div className="bg-[#F8FAFC] border border-[#E8E9EB] rounded-2xl p-5">
            {challenge.aiContent.split("\n\n").map((para, idx) => (
              <p key={idx} className={`text-sm text-[#3D4756] leading-[1.9] ${idx > 0 ? "mt-4" : ""}`}>
                {para}
              </p>
            ))}
          </div>
          <p className="text-[10px] text-[#8B95A1] mt-2 text-right font-medium">
            ⚠️ 이 내용에는 {challenge.errorCount}개의 오류가 포함되어 있어요
          </p>
        </div>

        {/* Divider */}
        <div className="flex items-center gap-4 mb-6">
          <div className="flex-1 h-px bg-[#F2F4F6]" />
          <div className="flex items-center gap-2 px-3 py-1.5 bg-red-50 rounded-full border border-red-200">
            <i className="ri-focus-3-line text-red-500 text-xs" />
            <span className="text-xs font-bold text-red-500">아래 항목 중 오류를 찾아보세요</span>
          </div>
          <div className="flex-1 h-px bg-[#F2F4F6]" />
        </div>
      </div>

      {/* Items */}
      <div className="px-8 space-y-3 mb-6">
        {challenge.items.map((item, idx) => (
          <AnalyzeItemCard
            key={item.id}
            item={item}
            index={idx}
            isSelected={selectedIds.has(item.id)}
            revealed={submitted}
            onToggle={() => toggle(item.id)}
          />
        ))}
      </div>

      {/* Submit / Score */}
      <div className="px-8 pb-8">
        {!submitted ? (
          <div>
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm text-[#8B95A1]">
                {selectedIds.size === 0
                  ? "오류라고 생각하는 항목을 선택하세요"
                  : `${selectedIds.size}개 선택됨 — 오류는 총 ${challenge.errorCount}개예요`}
              </p>
              {selectedIds.size > 0 && (
                <button
                  onClick={() => setSelectedIds(new Set())}
                  className="text-xs text-[#8B95A1] hover:text-[#191F28] transition-colors cursor-pointer whitespace-nowrap"
                >
                  초기화
                </button>
              )}
            </div>
            <button
              onClick={handleSubmit}
              disabled={selectedIds.size === 0}
              className={`w-full py-4 rounded-2xl font-extrabold text-base transition-all duration-200 whitespace-nowrap ${
                selectedIds.size > 0
                  ? "bg-[#191F28] text-white cursor-pointer hover:opacity-90 hover:-translate-y-0.5"
                  : "bg-[#E0E2E6] text-[#8B95A1] cursor-not-allowed"
              }`}
            >
              {selectedIds.size > 0 ? `${selectedIds.size}개 오류 제출하기 →` : "오류 항목을 먼저 선택해주세요"}
            </button>
          </div>
        ) : (
          <ScoreCard
            correct={correctHits}
            total={actualErrors.length}
            falsePosCount={falsePosCount}
            onNext={() => onComplete(`analyze-score-${correctHits}/${actualErrors.length}`)}
          />
        )}
      </div>
    </div>
  );
}
